<template>
    <button
        v-tippy="{ content: title, placement }"
        :aria-pressed="active"
        :data-variant="variant"
        :class="classesName"
        :disabled="disabled"
        @click="onClick"
      >
          <slot />
          <span
            v-if="loading"
            :class="classes.loading"
            :style="{ borderTopColor: variant === 'outline' ? 'currentColor' : '#ffffff' }"
          />
    </button>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  className: {
    type: String,
    default: ''
  },
  variant: {
    type: String,
    default: 'normal' // "navbar" | "accent" | "normal" | "outline" | "outlineNormal" | "custom" | "link"
  },
  size: {
    type: String,
    default: 'medium' // "big" | "medium" | "menu" | "small" | "tiny"
  },
  title: {
    type: String,
    default: ''
  },
  placement: {
    type: String,
    default: 'top'
  },
  active: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  onClick: {
    type: Function,
    default: () => { }
  }
});

const classes = {
  root: 'inline-flex items-center justify-center flex-shrink-0 font-bold leading-none rounded-full outline-none transition duration-300 ease-in-out focus:outline-none focus:shadow focus:ring-1',
  navbar: 'bg-primary-dark border border-solid border-button-accent text-white text-base hover:bg-button-accent-hover focus:bg-button-accent-hover hover:text-button-dark-hover focus:text-button-dark-hover',
  accent: 'bg-button-accent border border-button-accent text-button-dark text-base hover:bg-button-dark-hover focus:bg-button-accent-hover hover:text-button-accent-hover focus:text-button-dark-hover',
  normal: 'text-button-accent border border-transparent bg-button-dark hover:text-button-dark-hover hover:bg-button-accent-hover focus:text-button-accent-hover focus:bg-button-dark-hover focus:ring-button-dark-hover',
  submit: 'bg-orange text-light border border-transparent hover:bg-orange-hover focus:ring-orange-hover',
  link: 'underline text-button-dark font-semibold focus:outline-none hover:text-button-dark-hover focus:text-button-dark-hover hover:no-underline focus:no-underline',
  custom: 'border border-transparent',
  outline: 'border border-button-dark bg-transparent text-button-dark hover:text-light hover:bg-button-dark-hover hover:border-button-dark-hover focus:ring-button-dark-hover',
  outlineNormal: 'border border-border-base bg-transparent text-bolder !rounded-lg hover:bg-light hover:border-accent focus:border-accent focus:ring-0',
  outlineNeutral: 'border border-button-dark bg-accent-neutral text-button-dark hover:text-light hover:bg-button-dark-hover hover:border-button-dark-hover focus:ring-button-dark-hover',
  outlinePanel: 'border border-panel-button bg-transparent text-panel-button hover:text-panel-button-hover hover:bg-panel-button-hover hover:border-panel-button-hover focus:ring-panel-button-hover',
  loading: 'h-4 w-4 ms-2 rounded-full border-2 border-transparent border-t-2 animate-spin',
  disabled: 'border border-border-base bg-gray-300 border-border-400 text-body cursor-not-allowed',
  disabledOutline: 'border border-border-base text-muted cursor-not-allowed',
  tiny: 'px-5 py-0 h-6 text-xs',
  small: 'px-5 py-0 h-9 text-sm',
  menu: 'px-5 py-0 h-10',
  medium: 'px-5 py-0 h-12',
  big: 'px-6 py-0 h-15',
};

const classesName = computed(() => {
    return [
        classes.root,
        !props.disabled && props.variant === 'navbar' ? classes.navbar : '',
        !props.disabled && props.variant === 'accent' ? classes.accent : '',
        !props.disabled && props.variant === 'normal' ? classes.normal : '',
        !props.disabled && props.variant === 'submit' ? classes.submit : '',
        !props.disabled && props.variant === 'link' ? classes.link : '',
        props.disabled && props.variant === 'navbar' ? classes.disabled : '',
        props.disabled && props.variant === 'accent' ? classes.disabled : '',
        props.disabled && props.variant === 'normal' ? classes.disabled : '',
        props.disabled && props.variant === 'submit' ? classes.disabled : '',
        props.disabled && props.variant === 'link' ? classes.disabled : '',
        !props.disabled && props.variant === 'outline' ? classes.outline : '',
        props.disabled && props.variant === 'outline' ? classes.disabledOutline : '',
        !props.disabled && props.variant === 'outlineNormal' ? classes.outlineNormal : '',
        props.disabled && props.variant === 'outlineNormal' ? classes.disabledOutline : '',
        !props.disabled && props.variant === 'outlineNeutral' ? classes.outlineNeutral : '',
        props.disabled && props.variant === 'outlineNeutral' ? classes.disabledOutline : '',
        !props.disabled && props.variant === 'outlinePanel' ? classes.outlinePanel : '',
        props.disabled && props.variant === 'outlinePanel' ? classes.disabledOutline : '',
        props.size === 'tiny' ? classes.tiny : '',
        props.size === 'small' ? classes.small : '',
        props.size === 'menu' ? classes.menu : '',
        props.size === 'medium' ? classes.medium : '',
        props.size === 'big' ? classes.big : '',
        props.className
    ];
});

</script>
